/**
 * csvScoreParser.ts
 *
 * Parses bowling-alley CSV score exports into per-bowler lane/position/game entries.
 *
 * Expected CSV row format (South Point / standard bowling-center export):
 *   col[0]  – Lane number (integer ≥ 1)
 *   col[1]  – Letter, bowler name, or any label (ignored — position is inferred from order)
 *   col[2]  – Average (ignored)
 *   col[3]  – Handicap per game (ignored)
 *   col[4..len-4] – Game scores (variable count)
 *   col[len-3] – Total handicap (ignored)
 *   col[len-2] – Series total with handicap (ignored)
 *   col[len-1] – Scratch series total (ignored)
 *
 * Rows that do not start with a valid lane integer are treated as headers and skipped.
 *
 * The parser sorts all data rows by lane number (ascending) so that even if the CSV
 * is out of order the result is always lane-ordered. Within a lane the original row
 * order is preserved (stable sort). Each bowler is then assigned position 1, 2, 3 …
 * within their lane — matching the position numbers the admin assigns in the UI.
 */

export interface ParsedBowlerScore {
  lane: number;
  /** 1-based position within the lane (matches the admin-assigned "Position N") */
  position: number;
  /** Game scores in order — index 0 = Game 1, index 1 = Game 2, … */
  games: number[];
}

export interface CsvParseResult {
  entries: ParsedBowlerScore[];
  /** Human-readable notes about fallback mode or issues (not fatal errors). */
  warnings: string[];
}

/** Parse a single CSV line that may contain quoted fields. */
function parseCsvLine(line: string): string[] {
  const result: string[] = [];
  let current = '';
  let inQuotes = false;

  for (const char of line) {
    if (char === '"') {
      inQuotes = !inQuotes;
    } else if (char === ',' && !inQuotes) {
      result.push(current.trim());
      current = '';
    } else {
      current += char;
    }
  }
  result.push(current.trim());
  return result;
}

export function parseBowlingCsv(csvText: string): CsvParseResult {
  const warnings: string[] = [];
  const entries: ParsedBowlerScore[] = [];

  const lines = csvText
    .split('\n')
    .map(l => l.trim())
    .filter(l => l.length > 0);

  interface RawRow {
    lane: number;
    games: number[];
  }

  const rawRows: RawRow[] = [];

  for (const line of lines) {
    const fields = parseCsvLine(line);

    // Remove trailing empty fields caused by a trailing comma.
    while (fields.length > 0 && fields[fields.length - 1] === '') {
      fields.pop();
    }

    // Need at least: lane, label, avg, handicap, ≥1 game, hdcp_total, series_hdcp, scratch
    if (fields.length < 8) continue;

    const laneNum = parseInt(fields[0], 10);
    if (isNaN(laneNum) || laneNum < 1) continue; // header or non-data row

    // Game scores occupy indices 4 … (len-4) inclusive.
    // The last 3 fields are: total-hdcp, series-with-hdcp, scratch-series.
    const gameEndIdx = fields.length - 4;
    if (gameEndIdx < 4) continue; // would mean 0 game columns

    const games: number[] = [];
    for (let i = 4; i <= gameEndIdx; i++) {
      const score = parseInt(fields[i], 10);
      // Treat any parse failure as "no score" and skip.
      if (!isNaN(score) && score >= 0 && score <= 300) {
        games.push(score);
      }
    }

    if (games.length === 0) continue;

    rawRows.push({ lane: laneNum, games });
  }

  if (rawRows.length === 0) {
    warnings.push('No valid score rows found in this CSV file.');
    return { entries, warnings };
  }

  // ── Sort by lane (stable — preserves within-lane order from the CSV) ──────
  // This guarantees correct lane ordering regardless of how the bowling-center
  // software exported the file. Within a lane the original row order always
  // represents the bowling position (1st row = Position 1, 2nd = Position 2, …).
  const wasOrdered = rawRows.every((r, i) => i === 0 || r.lane >= rawRows[i - 1].lane);
  rawRows.sort((a, b) => a.lane - b.lane);
  if (!wasOrdered) {
    warnings.push('CSV rows were not in lane order — they have been sorted automatically.');
  }

  // ── Assign 1-based positions within each lane ─────────────────────────────
  let currentLane = -1;
  let positionInLane = 0;

  for (const row of rawRows) {
    if (row.lane !== currentLane) {
      currentLane = row.lane;
      positionInLane = 0;
    }
    positionInLane++;

    if (positionInLane <= 5) { // max 5 positions per lane
      entries.push({
        lane: row.lane,
        position: positionInLane,
        games: row.games,
      });
    }
    // More than 5 bowlers in one lane is silently ignored.
  }

  return { entries, warnings };
}
